import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowsCounterClockwise, CheckCircle, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function NeedsYou({ rows }) {
  if (!rows.length) return <div className="text-[11px] text-emerald-800" data-testid="buyer-refresh-all-set">Every buyer is fully connected.</div>;
  return (
    <ul className="space-y-2" data-testid="buyer-refresh-needs-you">
      {rows.map((b) => (
        <li key={b.page_id} className="border-t border-zinc-200 pt-2 text-[11px]" data-testid={`buyer-refresh-needs-${b.slug}`}>
          <div className="flex items-center gap-2">
            <Warning size={12} weight="bold" className="text-amber-700" /><b>{b.firm_name}</b>
            <Link to={b.org_id ? `/clients/${b.org_id}/manage` : "/admin/landing-pages"} className="ml-auto text-[10px] uppercase tracking-widest underline">
              {b.org_id ? "Open buyer" : "Landing pages"}
            </Link>
          </div>
          <ul className="list-disc pl-5 text-zinc-700">{b.issues.map((i) => <li key={i}>{i}</li>)}</ul>
        </li>
      ))}
    </ul>
  );
}

function RefreshResult({ res, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-none border-2 border-black sm:max-w-xl" data-testid="buyer-refresh-dialog">
        <DialogHeader><DialogTitle className="font-chivo text-xl font-black">Buyers brought up to date</DialogTitle></DialogHeader>
        <section>
          <div className="label-mono mb-1">Updated now ({res.fixed.length})</div>
          {res.fixed.length ? (
            <ul className="space-y-1 text-[11px]" data-testid="buyer-refresh-fixed">
              {res.fixed.map((f) => (
                <li key={f.email} className="flex gap-2"><CheckCircle size={12} weight="bold" className="mt-0.5 text-emerald-700" />
                  <span><b>{f.email}</b> · {f.changes.join(" · ")}</span></li>
              ))}
            </ul>
          ) : <div className="text-[11px] text-zinc-600" data-testid="buyer-refresh-nothing">Nothing needed updating. Every buyer login already sees all of its company's pages.</div>}
        </section>
        <section className="mt-3">
          <div className="label-mono mb-1">Still needs you ({res.needs_attention} of {res.total})</div>
          <NeedsYou rows={res.needs_you} />
        </section>
        <p className="mt-3 text-[10px] text-zinc-500">This also runs by itself every time you publish. It only adds missing links and never removes anything.</p>
      </DialogContent>
    </Dialog>
  );
}

const USERS_ADMIN = ["super_admin", "admin"];

export function BuyerRefreshButton({ onDone, className = "" }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const { user } = useAuth();
  if (user?.org_type !== "internal" || !USERS_ADMIN.includes(user?.role)) return null;
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/buyer-refresh");
      setRes(data);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't refresh buyers");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" onClick={run} disabled={busy} data-testid="buyer-refresh-btn" title="Give buyers added before the latest updates everything new"
              className={`border-2 border-black bg-white px-3 py-1.5 text-[10px] uppercase tracking-widest inline-flex items-center gap-1 hover:bg-black hover:text-white disabled:opacity-50 ${className}`}>
        <ArrowsCounterClockwise size={11} weight="bold" className={busy ? "animate-spin" : ""} /> {busy ? "Updating…" : "Bring all buyers up to date"}
      </button>
      {res && <RefreshResult res={res} onClose={() => setRes(null)} />}
    </>
  );
}
